import { Cartesian3, Cartographic, EllipsoidGeodesic } from "cesium";
import { getUsernameAndUserIdFormat, showToast } from "../Utility";
import Type from "../Types";

// ✅ CHANGE 1: keep signature (already added selectedEntitiesRef)
export async function mergeEntities(viewer, selectionResult, mapObjectsRef, setMapObjects, selectedEntitiesRef, userData) {
  const EPS_PT_METERS = 0.001; // point equality threshold in meters

  const emptyResult = () => ({
    mergesCount: 0,
    undo: {
      mutated: [],
      removed: [],
    },
  });

  try {
    const mapObjs = mapObjectsRef?.current || [];
    const modifier = getUsernameAndUserIdFormat(userData);

    // ✅ CHANGE 2: build a unified selection list from BOTH inputs (deduped)
    const unifiedSelection = [];
    const seen = new Set();
    const pushSel = (rec) => {
      if (!rec) return;
      const key = `${rec.id || ""}__${rec.dataSourceName || ""}`;
      if (seen.has(key)) return;
      seen.add(key);
      unifiedSelection.push(rec);
    };

    // from selectionResult (existing behavior)
    if (selectionResult?.results) {
      const resultsKey = selectionResult.results.f_f ? "f_f" : selectionResult.results.f_p ? "f_p" : null;
      if (resultsKey) {
        const selectedList = selectionResult.results[resultsKey] || [];
        for (const sel of selectedList) {
          pushSel({ id: sel?.id, dataSourceName: sel?.dataSourceName });
        }
      } else {
        // keep old validation path only if no ref-based selection exists
        const refList = selectedEntitiesRef?.current || selectedEntitiesRef || [];
        if (!Array.isArray(refList) || refList.length === 0) {
          showToast("Invalid result format.", Type.NOTIFICATION.ERROR);
          return emptyResult();
        }
      }
    }

    // from selectedEntitiesRef (new behavior)
    {
      const refList = selectedEntitiesRef?.current || selectedEntitiesRef || [];
      if (Array.isArray(refList) && refList.length) {
        for (const ent of refList) {
          const id = ent?.id;
          if (!id) continue;

          let dsName = null;
          for (const mo of mapObjs) {
            const ds = mo?.dataSource;
            if (!ds?.entities) continue;
            try {
              const byId = ds.entities.getById && ds.entities.getById(id);
              if (byId === ent) {
                dsName = mo?.name || mo?.dataSourceName || ds?.name || null;
                break;
              }
              if (byId && byId.id === ent.id) {
                dsName = mo?.name || mo?.dataSourceName || ds?.name || null;
                break;
              }
            } catch {}
          }

          pushSel({ id, dataSourceName: dsName, _directEntity: ent });
        }
      }
    }

    // ✅ CHANGE 3: unified "no selection" checks
    if (!unifiedSelection.length) {
      showToast("No items selected for merge.", Type.NOTIFICATION.INFO);
      return emptyResult();
    }

    // helper: read polyline points from a Cesium entity as [{x:lon,y:lat,z:height},...]
    function readEntityPoints(ent) {
      if (!ent || !ent.polyline) return null;
      const posProp = ent.polyline.positions;
      let cart3s = null;
      try {
        if (posProp && typeof posProp.getValue === "function") {
          cart3s = posProp.getValue(viewer?.clock?.currentTime);
        } else {
          cart3s = posProp;
        }
      } catch {
        cart3s = null;
      }
      if (!cart3s || !Array.isArray(cart3s) || cart3s.length === 0) return null;
      if (Array.isArray(cart3s[0])) {
        return cart3s.map((p) => ({ x: p[0], y: p[1], z: p[2] ?? 0 }));
      }
      // Cartesian3 -> lon/lat in degrees, keep height
      return cart3s.map((c) => {
        const carto = Cartographic.fromCartesian(c);
        return { x: (carto.longitude * 180) / Math.PI, y: (carto.latitude * 180) / Math.PI, z: carto.height };
      });
    }

    // helper: read properties snapshot
    function readEntityProps(ent) {
      if (!ent || !ent.properties) return null;
      const p = ent.properties;
      try {
        if (typeof p.getValue === "function") {
          return p.getValue(viewer?.clock?.currentTime);
        }
        return p;
      } catch {
        return p;
      }
    }

    // helper: distance in meters on WGS84 surface ignoring height
    function distanceMeters2D(a, b) {
      const c1 = Cartographic.fromDegrees(a.x, a.y, 0);
      const c2 = Cartographic.fromDegrees(b.x, b.y, 0);
      const geo = new EllipsoidGeodesic(c1, c2);
      return geo.surfaceDistance; // meters
    }

    // helper: compute length in meters (surface) ignoring height differences
    function lengthOf(points) {
      if (!points || points.length < 2) return 0;
      let s = 0;
      for (let i = 1; i < points.length; i++) s += distanceMeters2D(points[i - 1], points[i]);
      return s;
    }

    const entities = [];
    const missingIds = [];
    const originalById = new Map();

    // ✅ CHANGE 4: collect entities from datasources using unifiedSelection
    for (const sel of unifiedSelection) {
      const id = sel?.id;
      if (!id) continue;

      let found = false;

      // 4a) if direct entity is provided, resolve datasource from mapObjs
      if (sel._directEntity) {
        const direct = sel._directEntity;
        for (const mo of mapObjs) {
          const ds = mo?.dataSource;
          if (!ds?.entities) continue;
          let byId = null;
          try {
            byId = ds.entities.getById && ds.entities.getById(id);
          } catch {}
          if (byId === direct || (byId && direct && byId.id === direct.id)) {
            const pts = readEntityPoints(direct);
            if (!pts || pts.length === 0) break;

            const props = readEntityProps(direct);
            const rec = {
              id,
              dataSourceName: sel.dataSourceName || mo?.name || mo?.dataSourceName || ds?.name || null,
              mapObj: mo,
              dataSource: ds,
              cesiumEnt: direct,
              points: pts,
              properties: props,
              length: lengthOf(pts),
            };
            entities.push(rec);
            originalById.set(id, { points: pts, properties: props });
            found = true;
            break;
          }
        }
      }

      // 4b) fallback to old id-based lookup
      if (!found) {
        for (const mo of mapObjs) {
          const ds = mo?.dataSource;
          if (!ds || !ds.entities) continue;
          const cesEnt = ds.entities.getById && ds.entities.getById(id);
          if (cesEnt) {
            const pts = readEntityPoints(cesEnt);
            if (!pts || pts.length === 0) {
              found = false;
              break;
            }
            const props = readEntityProps(cesEnt);

            const rec = {
              id,
              dataSourceName: sel.dataSourceName,
              mapObj: mo,
              dataSource: ds,
              cesiumEnt: cesEnt,
              points: pts,
              properties: props,
              length: lengthOf(pts),
            };
            entities.push(rec);

            originalById.set(id, {
              points: pts,
              properties: props,
            });

            found = true;
            break;
          }
        }
      }

      if (!found) missingIds.push(id);
    }

    if (!entities.length) {
      showToast("No valid entities found for merging.", Type.NOTIFICATION.WARNING);
      if (missingIds.length) showToast(`Warning: ${missingIds.length} entities not found in loaded data.`, Type.NOTIFICATION.WARNING);
      return emptyResult();
    }

    const undoMutated = [];
    const undoRemoved = [];

    function ensureMutatedSnapshot(entRec) {
      if (!entRec || !entRec.cesiumEnt) return;
      const already = undoMutated.some((u) => u.entity === entRec.cesiumEnt);
      if (already) return;
      const orig = originalById.get(entRec.id);
      undoMutated.push({
        entity: entRec.cesiumEnt,
        dataSource: entRec.dataSource,
        pointsBefore: orig?.points || entRec.points,
        propertiesBefore: orig?.properties,
      });
    }

    function addRemovedSnapshot(entRec) {
      if (!entRec || !entRec.cesiumEnt) return;
      undoRemoved.push({
        entity: entRec.cesiumEnt,
        dataSource: entRec.dataSource,
      });
    }

    // merging loop
    let didMerge = true;
    let mergesCount = 0;

    while (didMerge) {
      didMerge = false;
      outer: for (let i = 0; i < entities.length; i++) {
        for (let j = 0; j < entities.length; j++) {
          if (i === j) continue;
          const A = entities[i];
          const B = entities[j];
          if (!A.points.length || !B.points.length) continue;
          if (A.dataSource !== B.dataSource) continue;
          const lastA = A.points[A.points.length - 1];
          const firstB = B.points[0];
          if (distanceMeters2D(lastA, firstB) < EPS_PT_METERS) {
            // merge A then B (drop duplicate)
            const mergedPts = [...A.points.slice(0, A.points.length), ...B.points.slice(1)];
            const mergedLen = lengthOf(mergedPts);

            // choose keeper by larger geometric length
            const keepIndex = A.length >= B.length ? i : j;
            const removeIndex = keepIndex === i ? j : i;
            const keeper = entities[keepIndex];
            const remover = entities[removeIndex];

            ensureMutatedSnapshot(keeper);
            addRemovedSnapshot(remover);

            // update geometry on keeper
            const flat = [];
            for (const p of mergedPts) flat.push(p.x, p.y, p.z ?? 0);
            const newCartesians = Cartesian3.fromDegreesArrayHeights(flat);
            try {
              if (keeper.cesiumEnt && keeper.cesiumEnt.polyline) {
                keeper.cesiumEnt.polyline.positions = newCartesians;
              } else if (keeper.cesiumEnt) {
                keeper.cesiumEnt.polyline = { positions: newCartesians };
              }
            } catch (e) {}

            // choose and set properties of larger entity
            try {
              const propsA = A.cesiumEnt.properties && typeof A.cesiumEnt.properties.getValue === "function" ? A.cesiumEnt.properties.getValue() : A.cesiumEnt.properties;
              const propsB = B.cesiumEnt.properties && typeof B.cesiumEnt.properties.getValue === "function" ? B.cesiumEnt.properties.getValue() : B.cesiumEnt.properties;
              const chosenProps = (A.length >= B.length ? propsA : propsB) ?? propsA ?? propsB;
              if (chosenProps && keeper.cesiumEnt) keeper.cesiumEnt.properties = chosenProps;

              if (modifier && keeper.cesiumEnt) {
                const currentProps =
                  keeper.cesiumEnt.properties && typeof keeper.cesiumEnt.properties.getValue === "function"
                    ? keeper.cesiumEnt.properties.getValue(viewer?.clock?.currentTime) || {}
                    : keeper.cesiumEnt.properties || {};

                keeper.cesiumEnt.properties = {
                  ...currentProps,
                  modifier,
                };
              }
            } catch {}

            // remove the other entity from datasource
            try {
              remover.dataSource.entities.remove(remover.cesiumEnt);
            } catch {}

            // update keeper metadata
            keeper.points = mergedPts;
            keeper.length = mergedLen;
            try {
              if (keeper.mapObj) keeper.mapObj.is_edited = true;
            } catch {}

            // remove from list
            entities.splice(removeIndex, 1);
            mergesCount++;
            didMerge = true;
            break outer;
          }
        }
      }
    }

    // final notifications
    if (mergesCount > 0) {
      showToast(`${mergesCount} merges completed.`, Type.NOTIFICATION.SUCCESS);
    } else {
      showToast("No merges performed.", Type.NOTIFICATION.INFO);
    }
    if (missingIds.length) {
      showToast(`Note: ${missingIds.length} entities were not found in loaded datasources.`, Type.NOTIFICATION.WARNING);
    }

    // update state to propagate changes
    try {
      setMapObjects([...mapObjectsRef.current]);
    } catch {}

    return {
      mergesCount,
      undo: {
        mutated: undoMutated,
        removed: undoRemoved,
      },
    };
  } catch (err) {
    try {
      showToast("Error during merge: " + (err?.message || String(err)), Type.NOTIFICATION.ERROR);
    } catch {}
    return emptyResult();
  }
}
